import { Filter, Sort } from '@/lib/types/v1'
import httpClient from '@/utils/httpClient'
import { useQuery } from '@tanstack/vue-query'
import { computed, ref, Ref, watchEffect } from 'vue'
import { QueryKeys, QueryParams } from '@/composables/helpers'
import {
    paginatedVocusMobileServersSchema,
    vocusMobileServerSchema,
} from '@/lib/schema/v1/vocus-mobile-server'

export type FindVocusMobileServerParams = {
    uuid: Ref<string>
    enabled?: Ref<boolean>
    staleTime?: Ref<number>
}

export const vocusMobileServerQueryKey = (uuid: string) => [
    QueryKeys.VocusMobileServers,
    `uuid:${uuid}`,
]

export const useFindVocusMobileServerQuery = (params: FindVocusMobileServerParams) => {
    const staleTime = ref(1000 * 60 * 5)
    const enabled = ref(true)
    const key = computed(() => vocusMobileServerQueryKey(params.uuid.value))

    watchEffect(() => {
        if (params.staleTime) {
            staleTime.value = params.staleTime.value
        }

        if (typeof params.enabled !== 'undefined') {
            enabled.value = params.enabled.value
        }
    })

    return useQuery({
        queryKey: key,
        queryFn: async () => {
            const { data } = await httpClient.get(`v1/vocus/mobile/servers/${params.uuid.value}`)

            return vocusMobileServerSchema.parse(data.data)
        },
        enabled,
        staleTime,
    })
}

export const useVocusMobileServerListQuery = (params?: QueryParams) => {
    const enabled = ref(true)
    const page = ref(1)
    const limit = ref(20)
    const sort = ref<Sort[] | undefined>()
    const filter = ref<Filter[] | undefined>()
    const q = ref()
    const view = ref('flat')
    const staleTime = ref(1000 * 60 * 5)
    const key = computed(() => [
        QueryKeys.VocusMobileServers,
        `q:${q.value}`,
        `page:${page.value}`,
        `limit:${limit.value}`,
        `sort:${JSON.stringify(sort.value)}`,
        `filter:${JSON.stringify(filter.value)}`,
        `view:${view.value}`,
    ])
    watchEffect(() => {
        if (params?.page?.value) {
            page.value = params.page.value
        }

        if (params?.limit?.value) {
            limit.value = params.limit.value
        }

        if (params?.sort?.value) {
            sort.value = params.sort.value
        }

        if (params?.filter?.value) {
            filter.value = params.filter.value
        }

        q.value = params?.q?.value

        if (params?.view?.value) {
            view.value = params.view.value
        }

        if (params?.staleTime?.value) {
            staleTime.value = params.staleTime.value
        }

        if (params?.enabled) {
            enabled.value = params.enabled?.value ?? true
        }
    })

    return useQuery({
        queryKey: key,
        queryFn: async () => {
            const { data } = await httpClient.get(`v1/vocus/mobile/servers`, {
                params: {
                    page: page.value,
                    limit: limit.value,
                    sort: sort.value,
                    filter: filter.value,
                    q: q.value,
                    view: view.value,
                },
            })

            return paginatedVocusMobileServersSchema.parse(data)
        },
        enabled,
        staleTime,
    })
}
